import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PageLoader from './PageLoader';
import Header from './Header';
import ServicesScrollSection from './ServicesScrollSection';
import KeyHighlightsSection from './KeyHighlightsSection';
import TrustSection from './TrustSection';
import IndustriesSection from './IndustriesSection';
import VideoIntroOverlay from './VideoIntroOverlay';
import InquiryPopup from './InquiryPopup';
import SpecialitySection from './SpecialitySection';
import OurClientsSection from './OurClientsSection';
import HomeTestimonialsCarousel from './HomeTestimonialsCarousel';
import Footer from './Footer';
import api from '../services/api';
import { STATIC_SERVICES_CATALOG } from '../data/staticServicesCatalog';
import {
  SITE_ORIGIN,
  OG_SHARE_IMAGE_URL,
  OG_SHARE_IMAGE_WIDTH,
  OG_SHARE_IMAGE_HEIGHT,
  OG_DEFAULT_TITLE,
  OG_DEFAULT_DESCRIPTION,
  TWITTER_DEFAULT_TITLE,
  TWITTER_DEFAULT_DESCRIPTION,
  SITE_BRAND_NAME,
  SITE_BRAND_LEGACY,
  SITE_BRAND_LEGACY_UPPER,
  SITE_BRAND_TAGLINE_SEO,
  SITE_BRAND_TRANSITION,
  SITE_LOGO_ALT,
  SITE_LOGO_PATH,
  SITE_CONTACT_CTA,
  SEO_PRIMARY_CITY,
  SEO_HOME_KEYWORDS
} from '../constants/siteBranding';
import './HomePage.css';

const LOADER_MIN_MS = 2200;

function HomePage() {
  const location = useLocation();
  const [services, setServices] = useState(() => STATIC_SERVICES_CATALOG);
  const [settings, setSettings] = useState(null);
  const [loaderDone, setLoaderDone] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [servicesRes, settingsRes] = await Promise.all([
        api.get('/services').catch(() => ({ data: null })),
        api.get('/settings').catch(() => ({ data: null }))
      ]);
      if (servicesRes?.data?.length) {
        setServices(servicesRes.data);
      }
      setSettings(settingsRes?.data || {
        popup_enabled: true,
        popup_initial_delay_seconds: 8,
        popup_increment_seconds: 2,
        animation_enabled: true,
        intro_video_enabled: true,
        intro_video_url: ''
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Scroll to hash (e.g. /#about) when navigating from another page
  useEffect(() => {
    if (!loaderDone) return;
    const hash = location.hash?.replace('#', '');
    if (hash) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loaderDone, location.hash]);

  useEffect(() => {
    if (!loaderDone) return;
    const sections = document.querySelectorAll(
      '.key-highlights-section, .services-scroll-section, .our-clients-section, .home-testimonials-section, .trust-section, .industries-section, .gallery-section, .downloads-section, .speciality-section'
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('in-view');
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );
    sections.forEach((el) => observer.observe(el));
    return () => sections.forEach((el) => observer.unobserve(el));
  }, [loaderDone]);

  const handleLoaderFinish = () => {
    setLoaderDone(true);
  };

  const showContent = loaderDone;

  if (!showContent) {
    return (
      <PageLoader
        onFinish={handleLoaderFinish}
        minDisplayTime={LOADER_MIN_MS}
      />
    );
  }

  const popupEnabled = settings?.popup_enabled !== false;
  const introVideoEnabled = settings?.intro_video_enabled !== false;

  const homeTitle = OG_DEFAULT_TITLE;
  const homeDescription = OG_DEFAULT_DESCRIPTION;

  return (
    <>
      <Helmet>
        <title>{homeTitle}</title>
        <meta name="description" content={homeDescription} />
        <meta
          name="keywords"
          content={`${SITE_BRAND_NAME}, ${SITE_BRAND_LEGACY}, ${SITE_BRAND_LEGACY_UPPER}, ${SEO_HOME_KEYWORDS}, office boy Ahmedabad, peon services Ahmedabad, labour supply Ahmedabad`}
        />
        <link rel="canonical" href={SITE_ORIGIN + '/'} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SITE_ORIGIN + '/'} />
        <meta property="og:title" content={homeTitle} />
        <meta property="og:description" content={homeDescription} />
        <meta property="og:image" content={OG_SHARE_IMAGE_URL} />
        <meta
          property="og:image:alt"
          content={`${SITE_LOGO_ALT} — facility management, valet parking & housekeeping in ${SEO_PRIMARY_CITY}`}
        />
        <meta property="og:image:width" content={String(OG_SHARE_IMAGE_WIDTH)} />
        <meta property="og:image:height" content={String(OG_SHARE_IMAGE_HEIGHT)} />
        <meta property="og:site_name" content={SITE_BRAND_NAME} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TWITTER_DEFAULT_TITLE} />
        <meta name="twitter:description" content={TWITTER_DEFAULT_DESCRIPTION} />
        <meta name="twitter:image" content={OG_SHARE_IMAGE_URL} />
        <meta name="twitter:image:alt" content={`${SITE_BRAND_NAME} — valet parking, facility management, housekeeping — Ahmedabad`} />
      </Helmet>
      <div className="home-page" id="home">
        <Header />
        <section className="home-brand-strip" aria-label="Company name and services">
          <div className="container">
            <div className="home-brand-logo-wrap" aria-hidden="true">
              <img
                src={`${process.env.PUBLIC_URL || ''}${SITE_LOGO_PATH}`}
                alt=""
                className="home-brand-strip-logo"
                width={240}
                height={80}
                decoding="async"
              />
            </div>
            <p className="home-brand-former" role="note">
              {SITE_BRAND_TRANSITION}
            </p>
            <h1 className="home-brand-h1">{SITE_BRAND_NAME}</h1>
            <p className="home-brand-tagline">{SITE_BRAND_TAGLINE_SEO}</p>
            <p className="home-brand-cta-hint">{SITE_CONTACT_CTA} by phone or WhatsApp for a free quote.</p>
          </div>
        </section>
        <ServicesScrollSection services={services} />
        <OurClientsSection />
        <HomeTestimonialsCarousel />
        <IndustriesSection />
        <div id="about">
          <KeyHighlightsSection />
        </div>
        <TrustSection />
        {/* <GallerySection images={galleryImages} /> */}
        {/* <DownloadsSection downloads={downloads} /> */}
        <SpecialitySection />
        <Footer />
      </div>
      {introVideoEnabled && !introComplete && (
        <VideoIntroOverlay
          onDismiss={() => setIntroComplete(true)}
          videoSrc={settings?.intro_video_url}
        />
      )}
      <InquiryPopup
        enabled={popupEnabled}
        initialDelaySeconds={settings?.popup_initial_delay_seconds ?? 8}
        incrementSeconds={settings?.popup_increment_seconds ?? 2}
      />
    </>
  );
}

export default HomePage;
